import { Component, OnInit, OnDestroy, ViewChild, HostListener, ViewChildren, AfterViewInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import * as Chart from 'chart.js';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { IssueService } from '../../../../services/issue.service';
import { getQueryParamFromAncestorRoute } from '../../../../utilities/route.utilities';

@Component({
    selector: 'issues-emergency-freq-chart',
    template: '<canvas id="basechart" #basechart *ngIf="data.length; else noData"></canvas><ng-template #noData><p class="text-center">No data available</p></ng-template>',
    styles: [':host { width: 100%; position: relative; display:block; }']
})
export class IssuesEmergencyFreqChartComponent implements OnDestroy, AfterViewInit {

    @ViewChildren('basechart') basechart: any;
    chart: any;
    subscriptions: any[] = [];
    debounceTimeout = 2000;
    debounceId: any;
    consumerId: string;
    clientId: number;
    data: any[] = [];
    pointColourData: any[] = [];
    labels: any[] = [];
    pipe: DatePipe = new DatePipe('en_GB');
    siteId: number;

    constructor(private issueService: IssueService,
        private route: ActivatedRoute,
        private router: Router) {
    }

    ngAfterViewInit(): void {
        this.route.queryParams.subscribe(queryParams => {
            this.onInitialise();
        });
    }

    onInitialise() {
        this.clientId = this.route.parent.parent.snapshot.params['clientId'];

        this.siteId = getQueryParamFromAncestorRoute(this.route, 'siteId');

        this.issueService.issuePaygEmergencyCreditFrequency(this.clientId, this.siteId).then((CreditFreqRes) => {
            let result = CreditFreqRes as any;

            this.data = CreditFreqRes
                .map(item => {
                    return {
                        x: item.instancesOfEmergencyCredit,
                        y: item.daysInEmergencyCredit
                    }
                });

            this.pointColourData = result
                .map(item => {
                    let colour = '';
                    if (item.colourBanding == 1) {
                        colour = '#ffed00'
                    }
                    if (item.colourBanding == 2) {
                        colour = '#ff9d00'
                    }
                    if (item.colourBanding == 3) {
                        colour = '#ff0000'
                    }
                    return colour;
                });

            this.labels = CreditFreqRes
                .map(item => {
                    return item.consumerName + "/" + item.consumerAddress;
                });

            if (this.data.length > 0) {
                this.createChart();
            }

        });

    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
        if (this.debounceId) {
            clearTimeout(this.debounceId);
        }
    }

    private getChartConfig(): any {
        let config = {
            type: 'scatter',
            options: {
                maintainAspectRatio: true,
                legend: {
                    display: false
                },
                tooltips: {
                    mode: 'index',
                    enabled: true,
                    backgroundColor: '#ffffff',
                    titleFontColor: '#153954',
                    bodyFontColor: '#153954',
                    borderWidth: 2,
                    borderColor: '#009aa3',
                    callbacks: {
                        label: function (tooltipItem, data) {
                            var label = data.labels[tooltipItem.index];
                            var labels = label.split("/");
                            labels[0] = "Name: " + labels[0];
                            labels[1] = "Address: " + labels[1];
                            return labels;
                        }
                    }
                },
                plugins: {
                    datalabels: {
                        formatter: function (value, context) {
                            return this.labels[context.dataIndex];
                        }
                    }
                },
                beforeInit: function (chart) {
                    chart.data.labels.forEach(function (e, i, a) {
                        if (/\n/.test(e)) {
                            a[i] = e.split(/\n/);
                        }
                    });
                },
                scales: {
                    xAxes: [{
                        type: 'linear',
                        ticks: {
                            beginAtZero: true
                        },
                        scaleLabel: {
                            display: true,
                            labelString: 'Frequency of use',
                            fontSize: 15,
                            fontStyle: "bold",
                        },
                    }],
                    yAxes: [
                        {
                            type: 'linear',
                            ticks: {
                                beginAtZero: true,
                                max: 60
                            },
                            scaleLabel: {
                                display: true,
                                labelString: 'Total days using E/C',
                                fontSize: 15,
                                fontStyle: "bold",
                            },
                        },
                    ]

                }
            }
        }

        return config;
    }

    @HostListener('window:resize', ['$event'])
    onResize(event: any) {
        this.createChart();
    }

    createChart() {
        if (this.chart != null) this.chart.destroy();
        if (!this.basechart) return;

        let datasets = [] as any;

        datasets.push({
            label: 'Emergency',
            data: this.data,
            backgroundColor: '#ff0000',
            pointRadius: 3,
            borderWidth: 2,
            pointHoverRadius: 8,
            pointHitRadius: 10,
            borderColor: '#009aa3',
            pointBorderColor: this.pointColourData,
            pointBackgroundColor: this.pointColourData,
            pointHoverBackgroundColor: this.pointColourData,
            pointHoverBorderColor: this.pointColourData,
        });

        let config = this.getChartConfig();
        let chartConfiguration: Chart.ChartConfiguration = {
            type: config.type,
            options: config.options,
            data: {
                labels: this.labels,
                datasets: datasets
            }

        };

        if (this.debounceId) {
            window.clearTimeout(this.debounceId);
        };

        this.debounceId = window.setTimeout(() => {
            if (this.chart != null) this.chart.destroy();
            if (!this.basechart || !this.basechart.first) return;
            let ctx = this.basechart.first.nativeElement;
            this.chart = new Chart(ctx, chartConfiguration);
        }, this.debounceTimeout);
    }


}
